/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import {
	BlogAuthorTagFragment,
	BlogCategoryFragment,
	BlogTagFragment,
	CosmosButtonFragment,
	CosmosEnumEdsGradients,
	CosmosModButtonFragment,
	CosmosRichTextFragment,
} from "@cosmos/hygraph-schema";
import { Avatar } from "@eds-web/react/Avatar";
import { useLocale } from "@epic-mw/i18n";
import { CosmosLink, CosmosRichText, CosmosDate, ButtonList, CosmosTag } from "@components/elements";
import { useCosmosComponentContext } from "@components/context";
import { css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { ColorfulTitle } from "@components/elements/molecules/title-desc-stack/ColorfulTitle.molecule";

const tagStyles = css({
	"--tag-hover-background-color": "token(colors.eds.fill.secondary.elevated.hover)",
});

export interface HeroArticleImageMainStackProps {
	authorTag?: BlogAuthorTagFragment | null;
	blogCategory?: BlogCategoryFragment | null;
	publicationDate?: string;
	title?: CosmosRichTextFragment | null;
	description?: CosmosRichTextFragment | null;
	buttons?: CosmosButtonFragment[] | null;
	modButtons?: CosmosModButtonFragment[] | null;
	tags?: BlogTagFragment[] | null;
	gradient?: CosmosEnumEdsGradients | null;
}

const sortFn = (a: BlogTagFragment, b: BlogTagFragment): number => {
	if (a.label < b.label) {
		return -1;
	}
	if (a.label > b.label) {
		return 1;
	}
	return 0;
};

export const HeroArticleImageMainStack = ({
	authorTag,
	blogCategory,
	publicationDate = "",
	title,
	description,
	buttons = [],
	modButtons = [],
	tags,
	gradient,
}: HeroArticleImageMainStackProps): React.JSX.Element => {
	const locale = useLocale();
	const { feedRootPath = "/feed" } = useCosmosComponentContext();

	const buttonSource = modButtons?.length ? modButtons : buttons;

	const sortedTags = (tags || []).sort(sortFn);
	return (
		<VStack alignItems="flex-start" gap="eds.dimension.24" className={tagStyles}>
			<HStack gap="eds.dimension.12">
				{blogCategory?.tag?.tagId ? (
					<CosmosTag href={`/${locale}${feedRootPath}?tags=${blogCategory?.tag?.tagId}`} label={blogCategory?.label} />
				) : (
					""
				)}
				<CosmosDate date={publicationDate} />
			</HStack>

			<ColorfulTitle
				gradient={gradient}
				title={{ ...title, variant: "heading2XL" }}
				className={css({
					smDown: {
						textStyle: "headingXL",
					},
				})}
			/>

			{description ? (
				<CosmosRichText
					html={description?.html}
					variant="paragraphXL"
					className={css({
						smDown: {
							textStyle: "paragraphLG",
						},
					})}
				/>
			) : (
				""
			)}
			{buttonSource?.length ? <ButtonList buttons={buttonSource} /> : null}
			{authorTag ? (
				<Avatar as={CosmosLink} href={authorTag?.href || ""} image={authorTag?.image?.url} label={authorTag?.label} />
			) : (
				""
			)}
			{sortedTags && sortedTags.length ? (
				<HStack flexWrap="wrap" gap="eds.dimension.8">
					{sortedTags.map((tag) => (
						<CosmosTag
							key={`tag-${tag.tagId}`}
							label={tag.label}
							href={`/${locale}${feedRootPath}?tags=${tag?.tagId}`}
						/>
					))}
				</HStack>
			) : null}
		</VStack>
	);
};
